import { useRouter } from 'expo-router';
import { createContext, useContext, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import {
  useContentLibraryClinicianGate,
  type ContentLibraryGateStatus,
} from '@/hooks/useContentLibraryClinicianGate';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import {
  trackContentLibraryGateViewed,
  trackContentLibraryOncologistConfirmed,
  trackContentLibraryOncologistDeclined,
} from '@/util/analyticsEvents';
import { env } from '@/util/env';

export type ContentLibraryGateSource = 'list' | 'item' | 'shared_link';

/**
 * Exposes the gate's resolved status (only ever 'bypassed' or 'attested',
 * since `children` never mounts otherwise) to descendants that need to know
 * it — e.g. `ContentLibraryItem`'s `isPublic` override — without each one
 * re-running `useContentLibraryClinicianGate()`'s own storage read. `null`
 * outside a `ContentLibraryGate` (e.g. the `(secure)` route, which renders
 * `ContentLibraryItem` unwrapped).
 */
const ContentLibraryGateContext = createContext<ContentLibraryGateStatus | null>(null);

export function useContentLibraryGateStatus(): ContentLibraryGateStatus | null {
  return useContext(ContentLibraryGateContext);
}

/** Fallback path segment when the partnership-interest survey id isn't configured yet, so the route still matches `[id].tsx` and degrades to its own "not available" state instead of a raw 404. */
const UNCONFIGURED_SURVEY_ID = 'unconfigured';

/**
 * Gates anonymous access to Content Library content behind a single
 * licensed-prescribing-clinician self-attestation. Authenticated sessions
 * render `children` immediately (see `useContentLibraryClinicianGate`); a
 * "yes" answer persists on-device and renders `children`; a "no" answer
 * routes to the public partnership-interest survey instead of rendering any
 * content — the survey itself is defined on the backend and referenced here
 * only by id (`EXPO_PUBLIC_CONTENT_LIBRARY_PARTNERSHIP_SURVEY_ID`), so it
 * renders its own "not available" state until that survey is published.
 * Both answers persist on-device, so a returning visitor who already
 * answered "no" sees a dedicated message instead of the question again.
 */
export function ContentLibraryGate({
  children,
  source,
}: {
  children: ReactNode;
  source: ContentLibraryGateSource;
}) {
  const { status, confirm, decline } = useContentLibraryClinicianGate();

  const router = useRouter();

  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    if (status === 'unattested' && !hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;

      trackContentLibraryGateViewed({ source });
    }
  }, [status, source]);

  if (status === 'checking') {
    return <Spinner className="py-24" size="large" />;
  }

  if (status === 'bypassed' || status === 'attested') {
    return (
      <ContentLibraryGateContext.Provider value={status}>
        {children}
      </ContentLibraryGateContext.Provider>
    );
  }

  if (status === 'declined') {
    return (
      <VStack className="mx-auto w-full max-w-[500px] flex-1 items-center justify-center gap-4 px-6 py-24">
        <Heading size="h2-md" className="text-center">
          Content Library isn&apos;t available to you
        </Heading>

        <Text className="text-center text-typography-700">
          You previously indicated you&apos;re not a licensed prescribing clinician. We&apos;ve
          shared your interest in partnering with our team.
        </Text>

        <Button
          action="primary"
          size="md"
          className="pt-2"
          onPress={() => router.replace(ROUTES.DIRECTORY)}
          {...getAccessibilityProps({ label: 'Go to Directory', role: 'button' })}>
          <ButtonText>Go to Directory</ButtonText>
        </Button>
      </VStack>
    );
  }

  return (
    <VStack className="mx-auto w-full max-w-[500px] flex-1 items-center justify-center gap-4 px-6 py-24">
      <Heading size="h2-md" className="text-center">
        I am a licensed prescribing clinician
      </Heading>

      <Text className="text-center text-typography-700">
        Content Library is intended for licensed prescribing clinicians. Let us know so we can show
        you the right content.
      </Text>

      <HStack className="gap-3 pt-2">
        <Button
          action="primary"
          size="md"
          onPress={() => {
            trackContentLibraryOncologistConfirmed({ source });

            void confirm();
          }}
          {...getAccessibilityProps({
            label: 'Yes, I am a licensed prescribing clinician',
            role: 'button',
          })}>
          <ButtonText>Yes</ButtonText>
        </Button>

        <Button
          action="secondary"
          variant="outline"
          size="md"
          onPress={() => {
            trackContentLibraryOncologistDeclined({ source });

            void decline();

            router.push(
              ROUTES.SURVEY(
                env.EXPO_PUBLIC_CONTENT_LIBRARY_PARTNERSHIP_SURVEY_ID || UNCONFIGURED_SURVEY_ID
              )
            );
          }}
          {...getAccessibilityProps({
            label: 'No, I am not a licensed prescribing clinician',
            role: 'button',
          })}>
          <ButtonText>No</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
